import { Point, HoveredHandle } from './types'
import { MapElement, MapSettings } from '../../../types'
import {
  isPointOnLine,
  isPointInRect,
  isPointInPolygon,
  isLineBasedElement,
} from './geometryUtils'

const isPointInElement = (point: Point, element: MapElement): boolean => {
  const tolerance = 10 // Click tolerance in pixels

  switch (element.type) {
    case 'wall':
    case 'door':
    case 'window':
    case 'line':
    case 'fixture-line':
    case 'measurement':
      if (element.properties.endPosition) {
        return isPointOnLine(
          point,
          element.position,
          element.properties.endPosition,
          tolerance,
        )
      }
      break

    case 'room':
    case 'stairs':
    case 'rectangle':
      if (element.properties.width && element.properties.height) {
        return isPointInRect(
          point,
          element.position,
          element.properties.width,
          element.properties.height,
        )
      }
      break

    case 'circle':
      if (element.properties.radius) {
        const distance = Math.sqrt(
          Math.pow(
            point.x - (element.position.x + element.properties.radius),
            2,
          ) +
            Math.pow(
              point.y - (element.position.y + element.properties.radius),
              2,
            ),
        )
        return distance <= element.properties.radius + tolerance
      }
      break

    case 'text':
      // Simple bounding box for text (approximate) with larger hit area
      const textWidth =
        (element.properties.text || '').length *
        (element.properties.fontSize || 16) *
        0.6
      const textHeight = element.properties.fontSize || 16
      const padding = 15 // Add padding for easier selection
      return isPointInRect(
        point,
        { x: element.position.x - padding, y: element.position.y - padding },
        textWidth + padding * 2,
        textHeight + padding * 2,
      )

    case 'poi':
      // Check if point is within POI icon area - make it larger for easier selection
      const distance = Math.sqrt(
        Math.pow(point.x - element.position.x, 2) +
          Math.pow(point.y - element.position.y, 2),
      )
      return distance <= 25 // Larger radius for easier grabbing

    case 'polygon':
      if (
        element.properties.vertices &&
        element.properties.vertices.length >= 3
      ) {
        return isPointInPolygon(point, element.properties.vertices as Point[])
      }
      break

    case 'fixture-rect':
      if (element.properties.width && element.properties.height) {
        return isPointInRect(
          point,
          element.position,
          element.properties.width,
          element.properties.height,
        )
      }
      break

    case 'fixture-point':
      // Check if point is within fixture point area
      const fixtureDistance = Math.sqrt(
        Math.pow(point.x - element.position.x, 2) +
          Math.pow(point.y - element.position.y, 2),
      )
      const fixtureSize = (element.properties.size || 20) / 2
      return fixtureDistance <= fixtureSize + tolerance

    case 'fixture-circle':
      if (element.properties.radius) {
        const distance = Math.sqrt(
          Math.pow(
            point.x - (element.position.x + element.properties.radius),
            2,
          ) +
            Math.pow(
              point.y - (element.position.y + element.properties.radius),
              2,
            ),
        )
        return distance <= element.properties.radius + tolerance
      }
      break

    case 'fixture-polygon':
      if (
        element.properties.vertices &&
        element.properties.vertices.length >= 3
      ) {
        return isPointInPolygon(point, element.properties.vertices as Point[])
      }
      break
  }

  return false
}

const getElementAtPoint = (
  point: Point,
  elements: MapElement[],
  mapSettings: MapSettings,
): MapElement | null => {
  // Check elements in reverse order (top to bottom rendering)
  for (let i = elements.length - 1; i >= 0; i--) {
    const element = elements[i]

    // Skip if element is on a different floor
    if (element.floor && element.floor !== mapSettings.currentFloor) continue

    if (isPointInElement(point, element)) {
      return element
    }
  }
  return null
}

const getHandleAtPoint = (
  point: Point,
  element: MapElement,
  setHoveredVertexIndex: (index: number) => void,
): HoveredHandle => {
  if (!isLineBasedElement(element)) {
    return null
  }

  const HANDLE_SIZE = 12 // Increased to match larger visual handles

  // Handle polygons and fixture-polygons
  if (
    (element.type === 'polygon' || element.type === 'fixture-polygon') &&
    element.properties.vertices
  ) {
    const vertices = element.properties.vertices as Point[]
    for (let i = 0; i < vertices.length; i++) {
      const vertex = vertices[i]
      const distance = Math.sqrt(
        Math.pow(point.x - vertex.x, 2) + Math.pow(point.y - vertex.y, 2),
      )
      if (distance <= HANDLE_SIZE) {
        setHoveredVertexIndex(i)
        return 'vertex'
      }
    }

    // Check for control point hits in curved edges
    if (element.curvedEdges && element.curvedEdges.length > 0) {
      const vertices = element.properties.vertices as Point[]

      for (const curvedEdge of element.curvedEdges) {
        const startVertex = vertices[curvedEdge.edgeIndex]
        const endVertex = vertices[(curvedEdge.edgeIndex + 1) % vertices.length]

        if (!startVertex || !endVertex) continue

        // Get control points
        let cp1 = curvedEdge.controlPoint1
        let cp2 = curvedEdge.controlPoint2

        // If control points are not set, calculate defaults
        if (
          !cp1 ||
          !cp2 ||
          (cp1.x === 0 && cp1.y === 0) ||
          (cp2.x === 0 && cp2.y === 0)
        ) {
          const dx = endVertex.x - startVertex.x
          const dy = endVertex.y - startVertex.y
          const perpX = -dy
          const perpY = dx
          const length = Math.sqrt(perpX * perpX + perpY * perpY)
          const normalizedPerpX = perpX / length
          const normalizedPerpY = perpY / length
          const curveDistance =
            ((curvedEdge.curvature ?? 50) / 100) * (length * 0.5)
          const offsetX = normalizedPerpX * curveDistance
          const offsetY = normalizedPerpY * curveDistance

          cp1 = {
            x: startVertex.x + dx * 0.33 + offsetX,
            y: startVertex.y + dy * 0.33 + offsetY,
          }
          cp2 = {
            x: startVertex.x + dx * 0.66 + offsetX,
            y: startVertex.y + dy * 0.66 + offsetY,
          }
        }

        // Check control point 1
        const distance1 = Math.sqrt(
          Math.pow(point.x - cp1.x, 2) + Math.pow(point.y - cp1.y, 2),
        )
        if (distance1 <= HANDLE_SIZE) {
          setHoveredVertexIndex(curvedEdge.edgeIndex)
          return 'controlPoint1'
        }

        // Check control point 2
        const distance2 = Math.sqrt(
          Math.pow(point.x - cp2.x, 2) + Math.pow(point.y - cp2.y, 2),
        )
        if (distance2 <= HANDLE_SIZE) {
          setHoveredVertexIndex(curvedEdge.edgeIndex)
          return 'controlPoint2'
        }
      }
    }

    return null
  }

  // Handle room/rectangle/stairs/circle and fixture-rect elements (corner handles)
  if (
    (element.type === 'room' ||
      element.type === 'rectangle' ||
      element.type === 'stairs' ||
      element.type === 'fixture-rect') &&
    element.properties.width &&
    element.properties.height
  ) {
    const corners = [
      { x: element.position.x, y: element.position.y }, // top-left
      {
        x: element.position.x + element.properties.width,
        y: element.position.y,
      }, // top-right
      {
        x: element.position.x + element.properties.width,
        y: element.position.y + element.properties.height,
      }, // bottom-right
      {
        x: element.position.x,
        y: element.position.y + element.properties.height,
      }, // bottom-left
    ]

    for (let i = 0; i < corners.length; i++) {
      const corner = corners[i]
      const distance = Math.sqrt(
        Math.pow(point.x - corner.x, 2) + Math.pow(point.y - corner.y, 2),
      )
      if (distance <= HANDLE_SIZE) {
        setHoveredVertexIndex(i)
        return 'vertex'
      }
    }
    return null
  }

  // Handle circle and fixture-circle elements (edge handles)
  if (
    (element.type === 'circle' || element.type === 'fixture-circle') &&
    element.properties.radius
  ) {
    const radius = element.properties.radius
    const center = {
      x: element.position.x + radius,
      y: element.position.y + radius,
    }
    const handles = [
      { x: center.x, y: center.y - radius }, // top
      { x: center.x + radius, y: center.y }, // right
      { x: center.x, y: center.y + radius }, // bottom
      { x: center.x - radius, y: center.y }, // left
    ]

    for (let i = 0; i < handles.length; i++) {
      const handle = handles[i]
      const distance = Math.sqrt(
        Math.pow(point.x - handle.x, 2) + Math.pow(point.y - handle.y, 2),
      )
      if (distance <= HANDLE_SIZE) {
        setHoveredVertexIndex(i)
        return 'vertex'
      }
    }
    return null
  }

  // Handle line-based elements (wall, door, window, line, fixture-line)
  if (!element.properties.endPosition) {
    return null
  }

  // Check start handle
  const startDistance = Math.sqrt(
    Math.pow(point.x - element.position.x, 2) +
      Math.pow(point.y - element.position.y, 2),
  )
  if (startDistance <= HANDLE_SIZE) {
    return 'start'
  }

  // Check end handle
  const endDistance = Math.sqrt(
    Math.pow(point.x - element.properties.endPosition.x, 2) +
      Math.pow(point.y - element.properties.endPosition.y, 2),
  )
  if (endDistance <= HANDLE_SIZE) {
    return 'end'
  }

  return null
}

export { isPointInElement, getElementAtPoint, getHandleAtPoint }
